'use client';

import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useCallback, useState } from 'react';

import { useToast } from '@ui/components/common/toast';
import {
  CreateProfessionalBookingData,
  CreateProfessionalBookingResult,
  createProfessionalBooking
} from '@website/services/bookings.service';

export interface UseCreateProfessionalBookingState {
  isCreating: boolean;
  isSuccess: boolean;
  error: string | null;
  bookingId: string | null;
  successMessage: string | null;
}

export interface UseCreateProfessionalBookingActions {
  createBooking: (data: CreateProfessionalBookingData) => Promise<CreateProfessionalBookingResult>;
  reset: () => void;
}

export interface UseCreateProfessionalBookingReturn
  extends UseCreateProfessionalBookingState,
    UseCreateProfessionalBookingActions {}

/**
 * Custom hook for creating professional bookings
 *
 * Provides state management, error handling, and toast notifications
 * for the professional booking creation process
 *
 * @example
 * ```tsx
 * const {
 *   isCreating,
 *   isSuccess,
 *   error,
 *   bookingId,
 *   createBooking,
 *   reset
 * } = useCreateProfessionalBooking();
 *
 * const handleSubmit = async () => {
 *   const result = await createBooking({
 *     activityId: 'activity-123',
 *     slotId: 'slot-456',
 *     // ... other booking data
 *   });
 *
 *   if (result.success) {
 *     // Booking created successfully
 *   }
 * };
 * ```
 */
export function useCreateProfessionalBooking(): UseCreateProfessionalBookingReturn {
  const { success: showSuccessToast, error: showErrorToast } = useToast();
  const t = useTranslations('TakeBooking.form.toast');
  const queryClient = useQueryClient();

  const [state, setState] = useState<UseCreateProfessionalBookingState>({
    isCreating: false,
    isSuccess: false,
    error: null,
    bookingId: null,
    successMessage: null
  });

  const createBooking = useCallback(
    async (data: CreateProfessionalBookingData): Promise<CreateProfessionalBookingResult> => {
      setState((prev) => ({
        ...prev,
        isCreating: true,
        error: null,
        isSuccess: false
      }));

      try {
        const result = await createProfessionalBooking(data);

        if (result.success) {
          setState((prev) => ({
            ...prev,
            isCreating: false,
            isSuccess: true,
            bookingId: result.booking?.id || null,
            successMessage: result.message || 'Booking created successfully!',
            error: null
          }));

          // Invalidate bookings queries to refetch updated data
          queryClient.invalidateQueries({ queryKey: ['bookings'] });
          queryClient.invalidateQueries({ queryKey: ['activity-bookings'] });

          showSuccessToast(t('bookingSuccessTitle'), t('bookingSuccessDescription'));
        } else {
          const errorMessage = result.error || 'Failed to create booking';
          setState((prev) => ({
            ...prev,
            isCreating: false,
            isSuccess: false,
            error: errorMessage,
            bookingId: null,
            successMessage: null
          }));

          // Show the actual error message from the backend
          showErrorToast(t('bookingFailedTitle'), errorMessage);
        }

        return result;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';

        setState((prev) => ({
          ...prev,
          isCreating: false,
          isSuccess: false,
          error: errorMessage,
          bookingId: null,
          successMessage: null
        }));

        // Show the actual error message from the exception
        showErrorToast(t('bookingFailedTitle'), errorMessage);

        return {
          success: false,
          error: errorMessage
        };
      }
    },
    [showSuccessToast, showErrorToast, t, queryClient]
  );

  const reset = useCallback(() => {
    setState({
      isCreating: false,
      isSuccess: false,
      error: null,
      bookingId: null,
      successMessage: null
    });
  }, []);

  return {
    ...state,
    createBooking,
    reset
  };
}
